/**
 * Garage Makers View
 * List of all makers with in-place detail panel
 */

import React, { useState, useEffect } from 'react';
import { Star, CheckCircle, X, ExternalLink, Mail, Briefcase, Bell, MessageCircle, Send, TrendingUp } from 'lucide-react';
import garageAPI from '../../../services/garage/api';
import GarageLoadingScreen from '../components/GarageLoadingScreen';
import AlertModal from '../modals/AlertModal';

const GarageMakersView = ({
  onNavigate,
  onToolLaunch,
  userEmail,
  userName,
  onNavigateToTool,
  initialMakerEmail, // New prop for cross-view navigation
  setDetailViewState // For controlling header back button
}) => {
  const [makers, setMakers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMaker, setSelectedMaker] = useState(null);
  const [makerDetails, setMakerDetails] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('tools');
  const [feedback, setFeedback] = useState([]);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [reviewFormData, setReviewFormData] = useState({
    selectedToolId: '',
    rating: 0,
    text: ''
  });
  const [submittingReview, setSubmittingReview] = useState(false);
  const [alertModal, setAlertModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    variant: 'info'
  });

  useEffect(() => {
    loadMakers();
  }, []);

  useEffect(() => {
    if (selectedMaker) {
      loadMakerDetails(selectedMaker);
    }
  }, [selectedMaker]);

  // Handle cross-view navigation: auto-open maker if initialMakerEmail is provided
  useEffect(() => {
    if (initialMakerEmail && initialMakerEmail !== selectedMaker) {
      handleMakerClick(initialMakerEmail);
    }
  }, [initialMakerEmail]);

  const loadMakers = async () => {
    try {
      setLoading(true);
      const data = await garageAPI.getMakers();
      setMakers(data);
    } catch (error) {
      console.error('Failed to load makers:', error);
      setMakers([]);
    } finally {
      setLoading(false);
    }
  };

  const loadMakerDetails = async (makerEmail) => {
    try {
      setDetailLoading(true);
      const data = await garageAPI.getMaker(makerEmail);
      setMakerDetails(data);
      await loadFeedback(data.tools || []);

      // Set header back button
      if (setDetailViewState) {
        setDetailViewState({
          isDetailView: true,
          backLabel: 'Back to Makers',
          onBack: closeDetailPanel
        });
      }
    } catch (error) {
      console.error('Failed to load maker details:', error);
      setMakerDetails(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const loadFeedback = async (tools) => {
    try {
      const allReviews = [];
      for (const tool of tools) {
        try {
          const toolReviews = await garageAPI.getReviews(tool.id);
          allReviews.push(...toolReviews.map(review => ({
            ...review,
            toolTitle: tool.title,
            toolId: tool.id
          })));
        } catch (error) {
          console.error(`Failed to load reviews for tool ${tool.id}:`, error);
        }
      }
      setFeedback(allReviews);
    } catch (error) {
      console.error('Failed to load feedback:', error);
      setFeedback([]);
    }
  };

  const handleSubmitReview = async (e) => {
    e.preventDefault();
    if (!reviewFormData.selectedToolId) {
      setAlertModal({
        isOpen: true,
        title: 'Validation Error',
        message: 'Please select a tool to review',
        variant: 'error'
      });
      return;
    }
    if (reviewFormData.rating === 0) {
      setAlertModal({
        isOpen: true,
        title: 'Validation Error',
        message: 'Please select a rating',
        variant: 'error'
      });
      return;
    }

    try {
      setSubmittingReview(true);
      await garageAPI.createReview({
        toolId: reviewFormData.selectedToolId,
        userEmail: userEmail,
        userName: userName || userEmail?.split('@')[0],
        rating: reviewFormData.rating,
        text: reviewFormData.text
      });

      setReviewFormData({ selectedToolId: '', rating: 0, text: '' });
      setShowReviewForm(false);
      await loadFeedback(makerDetails?.tools || []);
      setAlertModal({
        isOpen: true,
        title: 'Success',
        message: 'Review submitted successfully!',
        variant: 'success'
      });
    } catch (error) {
      console.error('Failed to submit review:', error);
      setAlertModal({
        isOpen: true,
        title: 'Error',
        message: 'Failed to submit review. Please try again.',
        variant: 'error'
      });
    } finally {
      setSubmittingReview(false);
    }
  };

  const getInitials = (name) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  const getColorForInitials = (initials) => {
    const colors = [
      'linear-gradient(135deg, #FF7A00 0%, #E55A00 100%)',
      'linear-gradient(135deg, #0684BC 0%, #034B6B 100%)',
      'linear-gradient(135deg, #0A8359 0%, #085041 100%)',
      'linear-gradient(135deg, #9B59B6 0%, #8E44AD 100%)'
    ];
    const index = initials.charCodeAt(0) % colors.length;
    return colors[index];
  };

  const getMemberSince = (date) => {
    return new Date(date).getFullYear();
  };

  const handleMakerClick = (makerEmail) => {
    setSelectedMaker(makerEmail);
    setActiveTab('tools');
  };

  const closeDetailPanel = () => {
    setSelectedMaker(null);
    setMakerDetails(null);
    setFeedback([]);
    setShowReviewForm(false);
    setReviewFormData({ selectedToolId: '', rating: 0, text: '' });
    // Clear header back button
    if (setDetailViewState) {
      setDetailViewState({ isDetailView: false, backLabel: '', onBack: null });
    }
  };

  return (
    <div className="garage-content garage-makers-view-container">
      {!selectedMaker ? (
        <>
          {/* Makers List View */}
          <div className="garage-page-hero">
            <div>
              <h1 className="garage-page-title">Makers</h1>
              <p className="garage-page-subtitle">
                Meet the creators building tools for RingCentral teams
              </p>
            </div>
          </div>

          <div className="garage-makers-list-section">
            {loading ? (
              <GarageLoadingScreen message="Loading makers..." />
            ) : makers.length === 0 ? (
              <div className="garage-empty">
                <div className="garage-empty-icon">👥</div>
                <h3>No makers yet</h3>
                <p>Be the first to create a tool!</p>
              </div>
            ) : (
              <div className="garage-makers-grid">
                {makers.map(maker => {
                  const initials = getInitials(maker.displayName);
                  return (
                    <div
                      key={maker.email}
                      className="garage-maker-card"
                      onClick={() => handleMakerClick(maker.email)}
                    >
                      <div
                        className="garage-maker-card-avatar"
                        style={{ background: getColorForInitials(initials) }}
                      >
                        {initials}
                      </div>
                      <div className="garage-maker-card-info">
                        <div className="garage-maker-card-name">
                          {maker.displayName}
                          {maker.isVerified && (
                            <CheckCircle size={16} className="garage-verified-badge-small" />
                          )}
                        </div>
                        <div className="garage-maker-card-title">
                          {maker.jobTitle}
                        </div>
                        <div className="garage-maker-card-dept">
                          {maker.department}
                        </div>
                      </div>
                      <div className="garage-maker-card-stats">
                        <div className="garage-maker-card-stat">
                          <span className="garage-maker-card-stat-value">{maker.totalTools}</span>
                          <span className="garage-maker-card-stat-label">tools</span>
                        </div>
                        <div className="garage-maker-card-stat">
                          <Star size={12} fill="var(--orange)" color="var(--orange)" />
                          <span className="garage-maker-card-stat-value">{maker.averageRating}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </>
      ) : (
        /* Maker Detail View - Replaces the list */
        <div className="garage-maker-detail-view-replacement">
          <div className="garage-maker-detail-content-full">
            {detailLoading ? (
              <GarageLoadingScreen message="Loading maker details..." />
            ) : !makerDetails ? (
              <div className="garage-empty">
                <p>Failed to load maker details</p>
              </div>
            ) : (
              <div className="garage-maker-profile-page">
                {/* Maker Profile Container */}
                <div className="garage-maker-profile-container">
                  <div className="garage-maker-profile-main-col">
                    {/* Maker Profile Header */}
                    <div className="garage-maker-profile-header">
                      <div className="garage-maker-profile-avatar-large">
                        {getInitials(makerDetails.displayName)}
                      </div>
                      <div className="garage-maker-profile-header-content">
                        <h1 className="garage-maker-profile-name">
                          {makerDetails.displayName}
                          {makerDetails.isVerified && (
                            <CheckCircle size={24} fill="var(--garage-blue)" color="white" />
                          )}
                        </h1>
                        <p className="garage-maker-profile-title">
                          {makerDetails.jobTitle} · {makerDetails.department}
                        </p>
                        {makerDetails.bio && (
                          <p className="garage-maker-profile-bio">{makerDetails.bio}</p>
                        )}

                        {/* Action Buttons */}
                        <div className="garage-maker-profile-actions">
                          <button className="garage-btn garage-btn-secondary">
                            <Bell size={16} />
                            Follow maker
                          </button>
                          <button className="garage-btn garage-btn-secondary" onClick={() => onNavigate && onNavigate('request-flow')}>
                            <MessageCircle size={16} />
                            Request a tool
                          </button>
                          <button className="garage-btn garage-btn-secondary">
                            <Mail size={16} />
                            Message
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Stats Bar */}
                    <div className="garage-maker-profile-stats-bar">
                      <div className="garage-maker-profile-stat">
                        <div className="garage-maker-profile-stat-label">Tools Shipped</div>
                        <div className="garage-maker-profile-stat-value">{makerDetails.totalTools || 0}</div>
                      </div>
                      <div className="garage-maker-profile-stat-divider"></div>
                      <div className="garage-maker-profile-stat">
                        <div className="garage-maker-profile-stat-label">Sessions</div>
                        <div className="garage-maker-profile-stat-value">{makerDetails.totalLaunches || 524}</div>
                      </div>
                      <div className="garage-maker-profile-stat-divider"></div>
                      <div className="garage-maker-profile-stat">
                        <div className="garage-maker-profile-stat-label">Avg Rating</div>
                        <div className="garage-maker-profile-stat-value">
                          {makerDetails.averageRating || '4.6'}
                          <Star size={16} fill="var(--garage-orange)" color="var(--garage-orange)" style={{ marginLeft: '4px' }} />
                        </div>
                      </div>
                      <div className="garage-maker-profile-stat-divider"></div>
                      <div className="garage-maker-profile-stat">
                        <div className="garage-maker-profile-stat-label">Member Since</div>
                        <div className="garage-maker-profile-stat-value">{getMemberSince(makerDetails.createdAt || new Date())}</div>
                      </div>
                    </div>

                    {/* Tabs */}
                    <div className="garage-maker-profile-tabs">
                      <button
                        className={`garage-maker-profile-tab ${activeTab === 'tools' ? 'active' : ''}`}
                        onClick={() => setActiveTab('tools')}
                      >
                        Tools
                      </button>
                      <button
                        className={`garage-maker-profile-tab ${activeTab === 'activity' ? 'active' : ''}`}
                        onClick={() => setActiveTab('activity')}
                      >
                        Activity
                      </button>
                      <button
                        className={`garage-maker-profile-tab ${activeTab === 'requests' ? 'active' : ''}`}
                        onClick={() => setActiveTab('requests')}
                      >
                        Open requests
                      </button>
                      <button
                        className={`garage-maker-profile-tab ${activeTab === 'feedback' ? 'active' : ''}`}
                        onClick={() => setActiveTab('feedback')}
                      >
                        Feedback
                      </button>
                    </div>

                    {/* Tab Content */}
                    <div className="garage-maker-profile-tab-content">
                      {activeTab === 'tools' && (
                        <div className="garage-tools-grid">
                          {!makerDetails.tools || makerDetails.tools.length === 0 ? (
                            <div className="garage-empty">
                              <p>No tools yet</p>
                            </div>
                          ) : (
                            makerDetails.tools.map((tool) => (
                              <div
                                key={tool.id}
                                className="garage-tool-card garage-tool-card-clickable"
                                onClick={() => onNavigateToTool && onNavigateToTool(tool.id)}
                                title={`View ${tool.title} details`}
                              >
                                <div className="garage-tool-card-header">
                                  {tool.imageUrl ? (
                                    <img src={tool.imageUrl} alt={tool.title} className="garage-tool-icon" />
                                  ) : (
                                    <div className="garage-tool-icon-placeholder">
                                      {tool.toolType === 'Gem' ? '💎' : '🔧'}
                                    </div>
                                  )}
                                  <div className="garage-tool-card-meta">
                                    <span className={`garage-tag garage-tag-sm garage-tag-${tool.toolType === 'App' ? 'blue' : 'orange'}`}>
                                      {tool.toolType}
                                    </span>
                                  </div>
                                </div>
                                <div className="garage-tool-card-body">
                                  <h3 className="garage-tool-card-title">{tool.title}</h3>
                                  <p className="garage-tool-card-description">{tool.description}</p>
                                </div>
                                <div className="garage-tool-card-footer">
                                  <div className="garage-tool-card-stats">
                                    <span className="garage-tool-stat">
                                      <TrendingUp size={14} />
                                      {tool.sessions7d || tool.totalLaunches || 0} sessions
                                    </span>
                                    <span className="garage-tool-stat">
                                      <Star size={14} fill="var(--garage-orange)" color="var(--garage-orange)" />
                                      {tool.rating || tool.averageRating || '4.5'}
                                    </span>
                                  </div>
                                  <button
                                    className="garage-btn garage-btn-primary garage-btn-sm"
                                    onClick={(e) => {
                                      e.stopPropagation(); // Prevent card click when clicking launch
                                      onToolLaunch && onToolLaunch(tool);
                                    }}
                                  >
                                    Launch
                                    <ExternalLink size={14} />
                                  </button>
                                </div>
                              </div>
                            ))
                          )}
                        </div>
                      )}

                      {activeTab === 'activity' && (
                        <div className="garage-placeholder-content">
                          <p className="garage-placeholder-text">Activity feed coming soon...</p>
                        </div>
                      )}

                      {activeTab === 'requests' && (
                        <div className="garage-placeholder-content">
                          <p className="garage-placeholder-text">Open requests coming soon...</p>
                        </div>
                      )}

                      {activeTab === 'feedback' && (
                        <div className="garage-feedback-section">
                          {/* Write Review Button */}
                          {!showReviewForm && makerDetails.tools && makerDetails.tools.length > 0 && (
                            <button
                              className="garage-btn garage-btn-primary"
                              onClick={() => setShowReviewForm(true)}
                              style={{ marginBottom: '20px' }}
                            >
                              <Star size={16} />
                              Write a Review
                            </button>
                          )}

                          {/* Review Form */}
                          {showReviewForm && (
                            <div className="garage-review-form-container">
                              <form onSubmit={handleSubmitReview} className="garage-review-form">
                                <h3>Write Your Review</h3>

                                {/* Tool Selection */}
                                <div className="garage-review-tool-select">
                                  <label>Select Tool *</label>
                                  <select
                                    value={reviewFormData.selectedToolId}
                                    onChange={(e) => setReviewFormData({ ...reviewFormData, selectedToolId: e.target.value })}
                                    className="garage-select"
                                    required
                                  >
                                    <option value="">-- Select a tool --</option>
                                    {makerDetails.tools?.map((tool) => (
                                      <option key={tool.id} value={tool.id}>{tool.title}</option>
                                    ))}
                                  </select>
                                </div>

                                {/* Star Rating */}
                                <div className="garage-review-rating-input">
                                  <label>Rating *</label>
                                  <div className="garage-star-rating">
                                    {[1, 2, 3, 4, 5].map((star) => (
                                      <button
                                        key={star}
                                        type="button"
                                        className="garage-star-btn"
                                        onClick={() => setReviewFormData({ ...reviewFormData, rating: star })}
                                      >
                                        <Star
                                          size={32}
                                          fill={star <= reviewFormData.rating ? '#FF7A00' : 'none'}
                                          color={star <= reviewFormData.rating ? '#FF7A00' : '#D0D0D0'}
                                        />
                                      </button>
                                    ))}
                                    <span className="garage-rating-text">
                                      {reviewFormData.rating > 0 ? `${reviewFormData.rating} star${reviewFormData.rating > 1 ? 's' : ''}` : 'Select rating'}
                                    </span>
                                  </div>
                                </div>

                                {/* Review Text */}
                                <div className="garage-review-text-input">
                                  <label>Your Review (Optional)</label>
                                  <textarea
                                    value={reviewFormData.text}
                                    onChange={(e) => setReviewFormData({ ...reviewFormData, text: e.target.value })}
                                    placeholder="Share your experience with this tool..."
                                    rows={4}
                                    className="garage-textarea"
                                  />
                                </div>

                                {/* Form Actions */}
                                <div className="garage-review-form-actions">
                                  <button
                                    type="button"
                                    className="garage-btn garage-btn-secondary"
                                    onClick={() => {
                                      setShowReviewForm(false);
                                      setReviewFormData({ selectedToolId: '', rating: 0, text: '' });
                                    }}
                                  >
                                    Cancel
                                  </button>
                                  <button
                                    type="submit"
                                    className="garage-btn garage-btn-primary"
                                    disabled={submittingReview}
                                  >
                                    <Send size={16} />
                                    {submittingReview ? 'Submitting...' : 'Submit Review'}
                                  </button>
                                </div>
                              </form>
                            </div>
                          )}

                          {/* Reviews List */}
                          <div className="garage-feedback-list">
                            <h3 style={{ marginBottom: '16px' }}>Reviews ({feedback.length})</h3>
                            {feedback.length === 0 ? (
                              <div className="garage-empty">
                                <p>No feedback yet</p>
                                {makerDetails.tools && makerDetails.tools.length > 0 && !showReviewForm && (
                                  <p style={{ fontSize: '14px', color: 'var(--garage-text-muted)', marginTop: '8px' }}>
                                    Be the first to review one of this maker's tools!
                                  </p>
                                )}
                              </div>
                            ) : (
                              feedback.map((review, index) => (
                                <div key={index} className="garage-feedback-item">
                                  <div className="garage-feedback-header">
                                    <div className="garage-feedback-tool">
                                      <strong>{review.toolTitle}</strong>
                                    </div>
                                    <div className="garage-feedback-rating">
                                      {[1, 2, 3, 4, 5].map((star) => (
                                        <Star
                                          key={star}
                                          size={14}
                                          fill={star <= review.rating ? 'var(--garage-orange)' : 'none'}
                                          color={star <= review.rating ? 'var(--garage-orange)' : 'var(--garage-border)'}
                                        />
                                      ))}
                                      <span className="garage-feedback-rating-value">{review.rating}/5</span>
                                    </div>
                                  </div>
                                  {review.text && (
                                    <p className="garage-feedback-comment">{review.text}</p>
                                  )}
                                  <div className="garage-feedback-meta">
                                    <span>{review.userName || review.userEmail || 'Anonymous'}</span>
                                    <span>•</span>
                                    <span>{new Date(review.createdAt).toLocaleDateString()}</span>
                                  </div>
                                </div>
                              ))
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Sidebar */}
                  <div className="garage-maker-profile-sidebar">
                    {/* Focus Areas */}
                    {makerDetails.focusAreas && makerDetails.focusAreas.length > 0 && (
                      <div className="garage-maker-sidebar-section">
                        <h3 className="garage-maker-sidebar-title">Focus Areas</h3>
                        <div className="garage-maker-sidebar-tags">
                          {makerDetails.focusAreas.map((area, idx) => (
                            <span key={idx} className="garage-tag">{area}</span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Reachable On */}
                    <div className="garage-maker-sidebar-section">
                      <h3 className="garage-maker-sidebar-title">Reachable On</h3>
                      <div className="garage-maker-sidebar-contact">
                        <div className="garage-maker-sidebar-contact-item">
                          <span className="garage-maker-sidebar-contact-icon">@</span>
                          <span>{makerDetails.email.split('@')[0]}</span>
                        </div>
                        <div className="garage-maker-sidebar-contact-item">
                          <Mail size={14} />
                          <span>{makerDetails.email}</span>
                        </div>
                        <div className="garage-maker-sidebar-contact-item">
                          <MessageCircle size={14} />
                          <span>rc@ringcentral.com</span>
                        </div>
                      </div>
                    </div>

                    {/* Office Hours */}
                    <div className="garage-maker-sidebar-section">
                      <h3 className="garage-maker-sidebar-title">Office Hours</h3>
                      <div className="garage-maker-sidebar-office">
                        <div>Thursdays · 2–4pm PT</div>
                        <div className="garage-text-muted">Drop in during Garage</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      <AlertModal
        isOpen={alertModal.isOpen}
        title={alertModal.title}
        message={alertModal.message}
        variant={alertModal.variant}
        onClose={() => setAlertModal({ ...alertModal, isOpen: false })}
      />
    </div>
  );
};

export default GarageMakersView;
